// EP-77 — operator (Capacitor app) FCM push.
//
// Everything here NO-OPS on non-native web (Capacitor.isNativePlatform() false)
// so the desktop build is unaffected, and every call is best-effort + non-fatal:
// a denied permission or an FCM hiccup must never break login/logout.
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { api } from '@/lib/api'
import i18n from '@/i18n'

function pushLocale(): 'en' | 'th' {
  return i18n.language?.startsWith('en') ? 'en' : 'th'
}

interface NativeVersions {
  app_version?: string
  os_version?: string
}

/**
 * EP-160: the app version (`App.getInfo()`) and the OS version
 * (`Device.getInfo()`) for the support console's device list. Each plugin is
 * read on its own, so one failing keeps the other's key; an older native
 * build without `@capacitor/device` just registers without `os_version`.
 * Never throws.
 */
async function getNativeVersions(): Promise<NativeVersions> {
  const versions: NativeVersions = {}
  try {
    const { version } = await App.getInfo()
    if (version) versions.app_version = version
  } catch {
    /* register without it */
  }
  try {
    const { osVersion } = await Device.getInfo()
    if (osVersion) versions.os_version = osVersion
  } catch {
    /* register without it */
  }
  return versions
}

async function postToken(token: string): Promise<void> {
  const versions = await getNativeVersions()
  await api.post('/api/device_tokens', {
    token,
    platform: Capacitor.getPlatform(), // ios | android
    locale: pushLocale(),
    ...versions,
  })
}

let rotationListenerAdded = false

/** Request permission, register the FCM token, and keep it fresh. Call after login. */
export async function registerOperatorPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    const perm = await FirebaseMessaging.requestPermissions()
    if (perm.receive !== 'granted') return
    const { token } = await FirebaseMessaging.getToken()
    if (token) await postToken(token)
    await addTokenRotationListener()
  } catch {
    /* best-effort */
  }
}

/** Delete the device's token server-side + locally. Call before logout (token still valid). */
export async function unregisterOperatorPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    const { token } = await FirebaseMessaging.getToken()
    if (token) await api.delete('/api/device_tokens', { data: { token } })
    await FirebaseMessaging.deleteToken()
  } catch {
    /* best-effort */
  }
}

async function addTokenRotationListener(): Promise<void> {
  if (rotationListenerAdded) return
  rotationListenerAdded = true
  await FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
    try {
      if (token) await postToken(token)
    } catch {
      /* best-effort */
    }
  })
}

/** Pure: extract the deep-link route from a tap event payload (server sets data.route). */
export function routeFromNotification(event: unknown): string | null {
  const data = (event as { notification?: { data?: Record<string, unknown> } })?.notification?.data
  const route = data?.route
  return typeof route === 'string' && route.startsWith('/') ? route : null
}

/** Wire notification taps → React Router navigation. Call once inside the Router. */
export async function setupPushDeepLinks(navigate: (path: string) => void): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const route = routeFromNotification(event)
      if (route) navigate(route)
    })
  } catch {
    /* best-effort */
  }
}

// Test seam — reset module state between specs.
export function __resetOperatorPushForTests(): void {
  rotationListenerAdded = false
}
